import { useEffect, useState } from "react";
import * as paths from "../constants/paths";
import { getAutocompleteRequestParams } from "../helpers/helpers";

const AUTOCOMPLETE_DEBOUNCE_MS = 150;

async function requestAutocomplete(query, type, signal) {
  const url = new URL("search-autocomplete", paths.API_OPENSEARCH_URL);
  url.search = new URLSearchParams(
    getAutocompleteRequestParams(query, type)
  ).toString();

  const response = await fetch(url.toString(), {
    credentials: "omit",
    signal,
  });

  if (!response.ok) {
    throw new Error(`Autocomplete request failed with ${response.status}`);
  }

  const data = await response.json();
  return data.status && Array.isArray(data.suggestions) ? data.suggestions : [];
}

export function useAutocompleteSuggestions({ enabled = true, query, type }) {
  const [suggestions, setSuggestions] = useState([]);
  const [isSuggestionsLoading, setIsSuggestionsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    const normalizedQuery = query?.trim() ?? "";

    setSuggestions([]);
    setIsSuggestionsLoading(false);
    setShowSuggestions(false);

    if (!enabled || normalizedQuery.length <= 1) {
      return undefined;
    }

    const controller = new AbortController();
    const timerId = setTimeout(async () => {
      setIsSuggestionsLoading(true);
      setShowSuggestions(true);

      try {
        const nextSuggestions = await requestAutocomplete(
          normalizedQuery,
          type || "address",
          controller.signal
        );

        if (!controller.signal.aborted) {
          setSuggestions(nextSuggestions);
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error("Autocomplete API error:", error);
          setSuggestions([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsSuggestionsLoading(false);
        }
      }
    }, AUTOCOMPLETE_DEBOUNCE_MS);

    return () => {
      clearTimeout(timerId);
      controller.abort();
    };
  }, [enabled, query, type]);

  return {
    isSuggestionsLoading,
    setShowSuggestions,
    setSuggestions,
    showSuggestions,
    suggestions,
  };
}
